import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { VMS_MAX_CAMERA_PANELS, VMS_SELECTION_COOKIE } from '../config/vms.config';

const isCameraId = (value) =>
  (typeof value === 'string' && value.trim() !== '') || (Number.isInteger(value) && value >= 0);

const isPanelIndex = (value) =>
  Number.isInteger(value) && value >= 0 && value < VMS_MAX_CAMERA_PANELS;

/** Restore valid camera slots without letting damaged preferences prevent viewing. */
const readCameraSelection = () => {
  try {
    const savedCameraIds = JSON.parse(Cookies.get(VMS_SELECTION_COOKIE) || '[]');
    if (!Array.isArray(savedCameraIds)) return [];

    const seenCameraIds = new Set();
    return savedCameraIds.slice(0, VMS_MAX_CAMERA_PANELS).map((cameraId) => {
      if (!isCameraId(cameraId) || seenCameraIds.has(String(cameraId))) return null;
      seenCameraIds.add(String(cameraId));
      return cameraId;
    });
  } catch {
    return [];
  }
};

/** Own the selected camera slots and their existing cookie preference. */
export default function useCameraSelection() {
  const [selectedCameraIds, setSelectedCameraIds] = useState(readCameraSelection);

  useEffect(() => {
    try {
      Cookies.set(VMS_SELECTION_COOKIE, JSON.stringify(selectedCameraIds));
    } catch {
      // Camera viewing still works when the browser blocks preference storage.
    }
  }, [selectedCameraIds]);

  const selectCamera = (panelIndex, cameraId) => {
    if (!isPanelIndex(panelIndex) || !isCameraId(cameraId)) return;

    setSelectedCameraIds((currentCameraIds) => {
      const isSelectedElsewhere = currentCameraIds.some(
        (currentId, index) =>
          index !== panelIndex && currentId !== null && String(currentId) === String(cameraId),
      );
      if (isSelectedElsewhere) return currentCameraIds;

      return Array.from(
        { length: Math.max(currentCameraIds.length, panelIndex + 1) },
        (_, index) => (index === panelIndex ? cameraId : (currentCameraIds[index] ?? null)),
      );
    });
  };

  const clearCamera = (panelIndex) => {
    if (!isPanelIndex(panelIndex)) return;
    setSelectedCameraIds((currentCameraIds) =>
      currentCameraIds.map((cameraId, index) => (index === panelIndex ? null : cameraId)),
    );
  };

  return {
    selectedCameraIds,
    selectCamera,
    clearCamera,
    clearAllCameras: () => setSelectedCameraIds([]),
  };
}
